/**
 * lane/constants — render-free constants for KanbanLane: drag MIME,
 * stable empty selection, the huge-lane render cap, and the colour palette.
 */

/** MIME used for card-move drags (set by `<KanbanCard>`). Hoisted to a
 *  named constant so the lane drop handler can branch off it without
 *  re-spelling the literal in three places. */
export const KANBAN_CARD_MIME = 'text/x-kanban-card-id'

/** ARC-10 linked cards — MIME stamped ALONGSIDE `KANBAN_CARD_MIME` when the
 *  grabbed tile is a projected placement instance (`card.placement_id` set).
 *  Payload = the placement id. Drop targets that read it route the drop to
 *  the placement PATCH (move THIS presence) instead of the card move
 *  endpoint (which would re-home the card and strand the placement). Old
 *  drop targets that ignore it keep working — they just move the home card. */
export const KANBAN_PLACEMENT_MIME = 'text/x-kanban-placement-id'

/** Stable empty set so `handleCardDragStart` has a non-null default
 *  without allocating a fresh Set on every render. */
export const EMPTY_SELECTION: ReadonlySet<string> = new Set<string>()

// PERF-2026-06-09-KANBAN-HUGE-LANE-RENDER-CAP — max card tiles a lane
// paints before collapsing the remainder behind a "Show all N" button.
// 150 comfortably covers any human-scale lane (so the cap is invisible
// in normal use) while preventing a triage/inbox lane with thousands
// of cards from mounting thousands of nodes at once and freezing the
// tab. The operator can still reveal everything on demand.
export const CARD_RENDER_CAP = 150

// Arc V7 (kanban perf roadmap) — windowed lane virtualization. Threshold
// sits in the plan's 40-50 band: lanes under it render exactly as today
// (zero risk), lanes over it mount only the visible window + overscan.
//
// 2026-07-09 default flipped ON: the flag began as a cautious staged
// rollout ("behind a localStorage flag while production KPI data (V1)
// sizes the rollout" — 08a148051 / 5c0617812), not a bug quarantine; no
// virtualization bug ever landed in the ledger. The keys now act as
// per-surface OPT-OUT kill switches — set the localStorage value to '0'
// to force the old flow render on that surface.
export const LANE_VIRTUALIZE_THRESHOLD = 48
export const LANE_VIRTUALIZE_FLAG_KEY = 'kanban:lane-virtualize'

// Arc V8 — the flat board is every operator's default landing surface
// (CRITICAL blast radius), so it keeps its own INDEPENDENT kill switch.
// Same threshold; a lane under it renders exactly as today on both
// surfaces.
export const LANE_VIRTUALIZE_FLAT_FLAG_KEY = 'kanban:lane-virtualize-flat'

// BUG-2026-07-06-KANBAN-LANE-COUNT-CAPPED: the per-lane card fetch is
// hard-capped server-side (views/cards/constants.py _UNPAGINATED_HARD_CAP
// = 2000), so `cards.length` on a mega-lane is the CAP, not the lane's
// real size (operator's Triage: 11,813 open cards shown as "2000"). When
// the loaded set hits the cap and the server's annotated `lane.card_count`
// says the lane is bigger, show the server truth instead.
export const LANE_FETCH_CAP = 2000

/**
 * I→O: (lane.card_count, loadedCount) → the number the count chip shows.
 * Loaded count wins whenever it is trustworthy (below the fetch cap, or
 * no server count available); the server count wins only when the fetch
 * was capped AND the server says the lane holds more.
 */
export function laneDisplayCount(
  serverCardCount: number | undefined | null,
  loadedCount: number,
): number {
  if (
    typeof serverCardCount === 'number' &&
    Number.isFinite(serverCardCount) &&
    loadedCount >= LANE_FETCH_CAP &&
    serverCardCount > loadedCount
  ) {
    return serverCardCount
  }
  return loadedCount
}

/** Trello-style 14-colour palette + a clear option (×). Matches the
 *  feel of the BOARD_THEMES preset palette but kept locally because
 *  lane colour is a thin user-pick surface, not a full theme. */
export const LANE_COLOR_PALETTE = [
  '#EF4444', // red
  '#F97316', // orange
  '#F59E0B', // amber
  '#EAB308', // yellow
  '#84CC16', // lime
  '#10B981', // emerald
  '#14B8A6', // teal
  '#06B6D4', // cyan
  '#0EA5E9', // sky
  '#3B82F6', // blue
  '#6366F1', // indigo
  '#8B5CF6', // violet
  '#EC4899', // pink
  '#94A3B8', // slate (neutral)
] as const
